import React from "react";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";
import { useOrg } from "@/hooks/useOrg";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { cn } from "@/lib/utils";
import {
  CircleIcon,
  CircleIconTone,
} from "@/components/DashboardOverviewDataCells/CircleIcon";
import { useTranslation } from "react-i18next";

const COLORS: CircleIconTone[] = [
  "indigo",
  "yellow",
  "lime",
  "orange",
  "emerald",
];

type MetricDisplay = Record<
  string,
  {
    icon?: React.ComponentType;
    color?: CircleIconTone;
  }
>;

export const MetricsBentoBox = ({
  metric,
  loadingCount = 4,
  display = {},
  className,
  showTitle = false,
  title,
}: {
  metric: OrganizationMetricsContextEnum;
  loadingCount?: number;
  display?: MetricDisplay;
  className?: string;
  showTitle?: boolean;
  title?: string;
}) => {
  const { org, loading: orgLoading, error: orgError } = useOrg();
  const { t } = useTranslation();

  const omissions = ["invitedFollowers"];

  const {
    loading: metricsLoading,
    error: metricsError,
    metrics,
    getPreviousCycleValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(org?.id, metric);

  if (metricsLoading || orgLoading) {
    return (
      <div>
        {showTitle && title && (
          <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
            {title}
          </h2>
        )}
        <div
          className={cn(
            "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4",
            className,
          )}
        >
          {[...Array(loadingCount)].map((_, idx) => {
            return (
              <DataCell
                key={idx}
                className="min-h-[142px]"
                isLoading={metricsLoading || orgLoading}
              />
            );
          })}
        </div>
      </div>
    );
  }

  if (metricsError || orgError) {
    return (
      <div>
        {showTitle && title && (
          <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
            {title}
          </h2>
        )}
        <div
          className={cn(
            "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4",
            className,
          )}
        >
          {[...Array(loadingCount)].map((_, idx) => {
            return (
              <DataCell
                key={idx}
                className="min-h-[142px]"
                title={t("metrics.loadingError")}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {showTitle && title && (
        <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
          {title}
        </h2>
      )}
      <div
        className={cn(
          "grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4",
          className,
        )}
      >
        {Object.entries(metrics || {})
          .filter(([name]) => !omissions.includes(name))
          .map(([name], idx) => {
            const customIcon = display[name || ""]?.icon;
            const DisplayIcon = customIcon;
            const color =
              display[name || ""]?.color || COLORS[idx % COLORS.length];

            return (
              <DataCell
                className="min-h-[142px]"
                key={name}
                isLoading={metricsLoading || orgLoading}
                title={name ? t(`metrics.${name}`) : undefined}
                tooltipText={t(`metrics.${name}ToolTip`, {
                  defaultValue: "",
                })}
                middleValue={getMetricDisplayValue(name, false, true)}
                bottomValue={
                  <PercentChange
                    value={getMetricCalculatedPercentChanged(name)}
                    prevCount={getPreviousCycleValue(name)}
                    customTemplate={t("metrics.comparisons.yearOverYear", {
                      percentChanged: "{{percentChange}}",
                    })}
                  />
                }
                icon={
                  DisplayIcon ? (
                    <CircleIcon color={color}>
                      <DisplayIcon />
                    </CircleIcon>
                  ) : undefined
                }
              />
            );
          })}
      </div>
    </div>
  );
};
